import { useState } from "react";
import { useTranslation } from "react-i18next";
import flagEn from "@/assets/flags/flag-en.webp";
import flagDe from "@/assets/flags/flag-de.webp";
import flagPl from "@/assets/flags/flag-pl.webp";
import flagNl from "@/assets/flags/flag-nl.webp";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { splitLocalePath } from "@/lib/locale";
import { ChevronLeft } from "lucide-react";
import {
  LOCALE_CURRENCIES,
  setCurrencyOverride,
  type CurrencyOverride,
} from "@/components/CurrencySelector";


type Language = "en" | "de" | "pl" | "nl";

const languages = [
  { code: "en" as Language, name: "English", flag: flagEn },
  { code: "de" as Language, name: "Deutsch", flag: flagDe },
  { code: "pl" as Language, name: "Polski", flag: flagPl },
  { code: "nl" as Language, name: "Nederlands", flag: flagNl },
];

export const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingLang, setPendingLang] = useState<Language | null>(null);

  const applyLanguage = (lang: Language) => {
    i18n.changeLanguage(lang);
    try {
      window.localStorage.setItem("i18nextLng", lang);
    } catch {
      /* storage blocked — i18next keeps the choice in memory */
    }

    // Deep-link safe: keep the current page and swap only the locale prefix,
    // e.g. /de/services/nutrition → /pl/services/nutrition.
    const { path } = splitLocalePath(window.location.pathname);
    const target = `/${lang}${path === "/" ? "" : path}${window.location.search}${window.location.hash}`;
    if (target !== window.location.pathname + window.location.search + window.location.hash) {
      window.location.assign(target);
    } else {
      window.dispatchEvent(new Event("currency-change"));
    }
  };

  const handleLanguageChange = (lang: Language) => {
    const options = LOCALE_CURRENCIES[lang] || [];
    if (options.length > 1) {
      // Step 2: ask which currency prices should be shown & charged in.
      setPendingLang(lang);
      return;
    }
    setIsOpen(false);
    applyLanguage(lang);
  };

  const handleCurrencyChange = (code: CurrencyOverride) => {
    if (!pendingLang) return;
    setCurrencyOverride(pendingLang, code);
    setIsOpen(false);
    const lang = pendingLang;
    setPendingLang(null);
    applyLanguage(lang);
  };

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];
  const pendingCurrencies = pendingLang ? LOCALE_CURRENCIES[pendingLang] || [] : [];

  const langButton = (code: Language, flag: string, label: string) => (
    <button
      onClick={() => handleLanguageChange(code)}
      className={`flex items-center justify-center p-2 rounded-lg transition-all hover:bg-primary/10 ${
        i18n.language === code ? "ring-2 ring-primary bg-primary/5" : ""
      }`}
      aria-label={label}
    >
      <img
        src={flag}
        alt={label}
        className="h-5 w-5 md:h-6 md:w-6 rounded-full object-cover shadow-sm"
      />
    </button>
  );

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setPendingLang(null);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-10 w-10 p-0 hover:bg-primary/10"
          aria-label="Select language"
        >
          <img
            src={currentLang?.flag}
            alt={currentLang?.name}
            className="h-6 w-6 rounded-full object-cover shadow-sm"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="end">
        {pendingLang ? (
          <div className="flex flex-col gap-1 min-w-[10rem]">
            <button
              onClick={() => setPendingLang(null)}
              className="flex items-center gap-1 px-2 pb-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="h-3 w-3" /> {languages.find((l) => l.code === pendingLang)?.name}
            </button>
            {pendingCurrencies.map((c) => (
              <button
                key={c.code}
                onClick={() => handleCurrencyChange(c.code)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all hover:bg-primary/10 text-sm font-medium text-foreground"
              >
                <span className="font-bold text-base">{c.symbol}</span>
                <span>{c.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {/* Top-left: English, Top-right: Polish */}
            {langButton("en", flagEn, "English")}
            {langButton("pl", flagPl, "Polski")}
            {/* Bottom-left: German, Bottom-right: Dutch */}
            {langButton("de", flagDe, "Deutsch")}
            {langButton("nl", flagNl, "Nederlands")}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

